import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import AuthContext from "../util/authcontext";
import { getFirestore, collection, query, orderBy, where, limit, startAfter, getDocs, doc, updateDoc, setDoc, deleteDoc, addDoc, writeBatch } from "firebase/firestore";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import firebaseApp from '../util/firebase';
import { Colors } from '../util/constants';
import { Dialog, DialogActions, DialogContent, DialogTitle, Box, Card, CardContent, Button, Typography, Grid, Avatar, IconButton, Stack, MenuItem, Select, InputLabel, FormControl, TextField } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { chunk } from "lodash";
import CircularProgress from "@mui/material/CircularProgress";
import LottieOrImageView from '../components/lottieimageorview';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const lockedLanguagePairs = [
  //{ source: "tr", target: "es" },
  //{ source: "tr", target: "en" },
  // add more pairs as needed
];

const storage = getStorage(firebaseApp);

const AudioPlayer = ({ filename, type, languageCode = "en" }) => {
  const playAudio = async () => {
    try {
      let audioSrc = "";

      if (type === 3) {
        const cleanAudioName = String(filename || "").replace(/\.mp3$/i, "");
        const mp3Path = `tts/${languageCode}/${cleanAudioName}.mp3`;

        const audioFileRef = storageRef(storage, mp3Path);
        audioSrc = await getDownloadURL(audioFileRef);
      } else {
        audioSrc = `/assets/audio/${filename}`;
      }

      const audio = new Audio(audioSrc);
      await audio.play();
    } catch (error) {
      console.error("Error playing audio:", error);
      console.log("filename:", filename);
      console.log("type:", type);
      console.log("languageCode:", languageCode);
    }
  };

  return (
    <button
      onClick={playAudio}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        margin: 0,
        fontSize: "1.2rem",
        cursor: "pointer",
      }}
    >
      ▶️
    </button>
  );
};

const LessonCard = React.memo(({ languagecode, lesson, fields, newFields, setNewFields, onEditLesson, onDeleteLesson, onEditField, onDeleteField, onAddField }) => {
    return (
        <Grid item xs={12} sm={6} md={4} lg={3} key={lesson.id}>
            <Card sx={{ minHeight: 180, backgroundColor: "#f5f5f5", borderRadius: "10px", position: "relative" }}>
                <CardContent>
                    {/* 🔹 Lesson Name (Click to Edit) */}
                    <Typography
                        variant="h6"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => onEditLesson(lesson)}
                    >
                        {lesson.name}
                    </Typography>

                    {/* 🔹 Lesson Points */}
                    <Typography variant="body2" color="text.secondary">
                        %{lesson.points}
                    </Typography>

                    {/* 🔹 Delete Button (Top Right) */}
                    <IconButton
                        onClick={() => onDeleteLesson(lesson.id)}
                        color="error"
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <DeleteIcon />
                    </IconButton>

                    {/* 🔹 Lesson Fields */}
                    <Box mt={1}>
                        {fields.map((field) => (
                            <Box key={field.id} display="flex" flexDirection="column" gap={0.5}>

                                {/* FIRST ROW */}
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography
                                        variant="subtitle2"
                                        color="textSecondary"
                                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => onEditField(field)}
                                        dangerouslySetInnerHTML={{
                                            __html: field.translation
                                                ? `${field.name} - ${field.translation}`
                                                : field.name
                                        }}
                                    />

                                    {field.image && (
                                        <LottieOrImageView filename={field.image} width={30} height={30} />
                                    )}

                                    {field.audio && (
                                    <AudioPlayer
                                        filename={`${languagecode}_${field.audio}.mp3`}
                                        type={lesson.type}
                                        languageCode={languagecode}
                                    />
                                    )}

                                    <IconButton size="small" onClick={() => onDeleteField(field.id)}>
                                        <DeleteIcon fontSize="small" color="error" />
                                    </IconButton>
                                </Box>

                                {field.translation && (
                                    <>
                                        {/* SECOND ROW */}
                                        <Box display="flex" alignItems="center" gap={1} sx={{ pl: 0.5 }}>
                                            {field.question?.includes("___") ? (
                                                <Typography variant="caption">
                                                    q: {field.question}
                                                </Typography>
                                            ) : (
                                                <HelpOutlineIcon sx={{ fontSize: 16, color: "warning.main" }} />
                                            )}

                                            {field.answer?.trim().length > 0 ? (
                                                <Typography variant="caption">
                                                    a: {field.answer}
                                                </Typography>
                                            ) : (
                                                <HelpOutlineIcon sx={{ fontSize: 16, color: "warning.main" }} />
                                            )}
                                        </Box>
                                    </>
                                )}

                            </Box>
                        ))}
                    </Box>

                    {/* 🔹 Add New Field Input */}
                    <Box display="flex" alignItems="center" mt={2}>
                        <TextField
                            label="New Field Name"
                            size="small"
                            fullWidth
                            variant="outlined"
                            value={newFields[lesson.id]?.name || ""} // ✅ Use `lesson.id`
                            onChange={(e) =>
                                setNewFields(prev => ({
                                    ...prev,
                                    [lesson.id]: {
                                        ...(prev[lesson.id] || { lessonid: lesson.id, name: "", type: lesson.type }), // ✅ Ensure default values exist
                                        name: e.target.value
                                    }
                                }))
                            }
                        />
                        <IconButton onClick={() => onAddField(lesson.id)} sx={{ ml: 1 }}>
                            <AddIcon color="primary" />
                        </IconButton>
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );
});

export default function Cms() {
    const { uservalue, languagevalue, loaded } = useContext(AuthContext);
    const [user] = uservalue;
    const [language] = languagevalue
    const navigate = useNavigate();
    const location = useLocation();
    const db = getFirestore(firebaseApp);

    const [selectedLevel, setSelectedLevel] = useState(1);

    const [openAddSection, setOpenAddSection] = useState(false);
    const [newSection, setNewSection] = useState({
        name: "",
        languagecode: language.languagecode,
        level: "",
        order: "",
        image: "",
        lessons: [],
        lessontypes: [],
    });
    const [editingSection, setEditingSection] = useState(null); // ID of section being edited
    const [updatedSection, setUpdatedSection] = useState(null);

    const [sections, setSections] = useState([]);

    const [newLesson] = useState({
        explanation: "",
        image: "",
        languagecode: language.languagecode,
        name: "",
        order: 0, // Will be auto-calculated
        points: 0,
        sourcelanguagecode: language.sourcelanguagecode,
        type: 1,
        vocabularycount: 0
    });
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedLesson, setSelectedLesson] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [lessonFields, setLessonFields] = useState([]);

    const [selectedLessonSectionId, setSelectedLessonSectionId] = useState("");

    const [openFieldDialog, setOpenFieldDialog] = useState(false);
    const [selectedField, setSelectedField] = useState(null);
    const [newFields, setNewFields] = useState({});

    const [editingLesson, setEditingLesson] = useState(null);

    const [isAllowed, setIsAllowed] = useState(null); // Tracks if user is allowed

    const [lastSection, setLastSection] = useState(null); // Track last document for pagination
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true); // To stop fetching when no more data
    const limitValue = 3;

    const [organizing, setOrganizing] = useState(false);

    // Check if the current pair is locked
    const isLocked = lockedLanguagePairs.some(
    pair =>
        pair.source === language.sourcelanguagecode &&
        pair.target === language.languagecode
    );

    const getImage = (filename) => `/assets/images/${filename}.png`;

    useEffect(() => {
        if (loaded) {
            if (user?.user?.userid === "pxWqLAyFaseogcrRZAm70Sf4OYp1") {
                setIsAllowed(true);  // Allow access
            } else {
                navigate("/login", { replace: true }); // Redirect immediately
            }
        }
    }, [user, loaded, navigate]);

    const fetchData = useCallback(async (isFirstLoad = false) => {
        if (isLoading || !hasMore) return;

        setIsLoading(true); // ✅ Prevent duplicate requests
        console.log("Fetching sections...");

        try {
            const sectionsDocName = `sections${language.languagecode}${language.sourcelanguagecode}`;
            let sectionsQuery = collection(db, sectionsDocName);

            // 🔹 Apply correct pagination
            if (lastSection && !isFirstLoad) {
                console.log("Fetching sections after:", lastSection.id);
                sectionsQuery = query(
                    sectionsQuery,
                    orderBy("order", "asc"), // ✅ Order consistency
                    orderBy("__name__"), // ✅ Ensure uniqueness
                    startAfter(lastSection), // ✅ Fetch next batch
                    limit(limitValue)
                );
            } else {
                console.log("Fetching initial sections...");
                sectionsQuery = query(
                    sectionsQuery,
                    orderBy("order", "asc"),
                    orderBy("__name__"),
                    limit(limitValue)
                );
            }

            const sectionsSnap = await getDocs(sectionsQuery);

            if (sectionsSnap.empty) {
                console.log("No more sections to fetch.");
                setHasMore(false);
                setIsLoading(false);
                return;
            }

            const sectionsData = sectionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // 🔹 Append new sections while **removing duplicates**
            setSections(prevSections => {
                const updatedSections = [...prevSections, ...sectionsData];
                return updatedSections.filter(
                    (section, index, self) => self.findIndex(s => s.id === section.id) === index
                );
            });


            // 🔹 Ensure `lastSection` is updated **only if new sections were fetched**
            const newLastSection = sectionsSnap.docs[sectionsSnap.docs.length - 1];
            if (newLastSection?.id !== lastSection?.id) {
                setLastSection(newLastSection);
            }

            // 🔹 Extract lesson IDs from **ONLY the newly fetched sections**
            const lessonIds = sectionsData.flatMap(section => section.lessons);
            console.log("Lesson IDs to fetch:", lessonIds);

            if (lessonIds.length === 0) {
                setIsLoading(false);
                return;
            }

            // 🔹 Query lessons in chunks 
            const lessonChunks = chunk(lessonIds, 40);
            let allLessons = new Map(isFirstLoad ? [] : lessons.map(l => [l.id, l]));

            for (const lessonChunk of lessonChunks) {
                const lessonsDocName = `lessons${language.languagecode}${language.sourcelanguagecode}`;
                const lessonsQuery = query(collection(db, lessonsDocName), where("__name__", "in", lessonChunk));
                const lessonsSnap = await getDocs(lessonsQuery);
                const lessonsData = lessonsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                lessonsData.forEach(lesson => allLessons.set(lesson.id, lesson));
            }

            // 🔹 Convert Map to array & update state
            setLessons(Array.from(allLessons.values()));

            // 🔹 Query lesson fields in chunks of 30
            const fieldChunks = chunk(lessonIds, 30);
            let allLessonFields = new Map(isFirstLoad ? [] : lessonFields.map(f => [f.id, f]));

            for (const fieldChunk of fieldChunks) {
                const fieldsDocName = `fields${language.languagecode}${language.sourcelanguagecode}`;
                const lessonFieldsQuery = query(collection(db, fieldsDocName), where("lessonid", "in", fieldChunk));
                const lessonFieldsSnap = await getDocs(lessonFieldsQuery);
                const lessonFieldsData = lessonFieldsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                lessonFieldsData.forEach(field => allLessonFields.set(field.id, field));
            }

            // 🔹 Convert Map back to array & update state
            setLessonFields(Array.from(allLessonFields.values()));

        } catch (error) {
            console.error("❌ Error fetching sections, lessons, and fields:", error);
        } finally {
            setIsLoading(false);
        }
    }, [db, hasMore, isLoading, lastSection, language, lessonFields, lessons]);

    useEffect(() => {
        fetchData();
    }, [fetchData]); // ✅ No warning now

    // ✅ Set background color once
    useEffect(() => {
        document.body.style.background = Colors[14];
    }, []);

    // ✅ Infinite Scroll
    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100 &&
                !isLoading &&
                hasMore
            ) {
                fetchData();
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [fetchData, isLoading, hasMore]); // ✅ No warning now

    if (!loaded || isAllowed === null) {
        return null;
    }

    const handleAddSection = async () => {
        if (isLocked) return;
        if (!newSection.name || !newSection.level || !newSection.order) {
            alert("Please fill in all required fields.");
            return;
        }

        try {
            const newDocRef = await addDoc(collection(db, `sections${language.languagecode}${language.sourcelanguagecode}`), newSection);

            setSections(prevSections =>
                [...prevSections, { id: newDocRef.id, ...newSection }]
                    .sort((a, b) => a.order - b.order) // ✅ Sort sections immediately
            );

            // Reset form and close modal
            setNewSection({ name: "", languagecode: language.languagecode, level: 1, order: 0, image: "", lessons: [], lessontypes: [] });
            setOpenAddSection(false); // 🔹 Close modal after adding section
        } catch (error) {
            console.error("Error adding section:", error);
        }
    };

    const handleEditSection = (section) => {
        if (isLocked) return;
        setEditingSection(section.id);
        setUpdatedSection({ ...section }); // Store section details in state
    };

    const handleUpdateSection = async () => {
        if (isLocked) return;
        if (!updatedSection) return;

        try {
            const docRef = doc(db, `sections${language.languagecode}${language.sourcelanguagecode}`, updatedSection.id);
            const { id, ...sectionData } = updatedSection;

            await setDoc(docRef, sectionData, { merge: true });

            setSections(prevSections =>
                prevSections
                    .map(s => (s.id === updatedSection.id ? { ...updatedSection, id: s.id } : s))
                    .sort((a, b) => a.order - b.order)
            );

            setEditingSection(null);
            setUpdatedSection(null);
        } catch (error) {
            console.error("Error updating section:", error);
        }
    };

    const handleDeleteSection = async (sectionId) => {
        if (isLocked) return;
        if (!window.confirm("Are you sure you want to delete this section?")) return;

        try {
            await deleteDoc(doc(db, `sections${language.languagecode}${language.sourcelanguagecode}`, sectionId));

            // Remove from UI
            setSections(prevSections => prevSections.filter(section => section.id !== sectionId));
        } catch (error) {
            console.error("Error deleting section:", error);
        }
    };

    const handleAddLesson = async (section) => {
        if (isLocked) return;
        try {
            // 🔹 Step 1: Determine new lesson order inside this section
            const orderedLessons = section.lessons
                .map(lessonId => lessons.find(l => l.id === lessonId)) // ✅ Use id instead of order
                .filter(l => l)
                .sort((a, b) => a.order - b.order);

            let newOrder = orderedLessons.length > 0
                ? orderedLessons[orderedLessons.length - 1].order + 1
                : 1; // ✅ Assign the next available order

            // 🔹 Step 2: Create the new lesson object
            const newLessonData = {
                ...newLesson,
                name: "New Lesson",
                order: newOrder, // ✅ Assign floating order
                image: "default-image",
                type: newLesson.type || 1 // ✅ Ensure default type if missing
            };

            // 🔹 Step 3: Add the new lesson to Firestore
            const newDocRef = await addDoc(collection(db, `lessons${language.languagecode}${language.sourcelanguagecode}`), newLessonData);
            const newLessonId = newDocRef.id;

            // 🔹 Step 4: Find correct index for insertion in `lessons` array
            let updatedLessonsArray = [...section.lessons];
            let updatedLessonTypesArray = [...section.lessontypes || []]; // ✅ Ensure array exists

            let insertIndex = updatedLessonsArray.findIndex(id => {
                const lesson = lessons.find(l => l.id === id);
                return lesson && lesson.order > newOrder;
            });

            if (insertIndex === -1) {
                insertIndex = updatedLessonsArray.length; // ✅ Append at the end if no larger order found
            }

            // 🔹 Step 5: Insert new lesson ID and type at the correct index
            updatedLessonsArray.splice(insertIndex, 0, newLessonId);
            updatedLessonTypesArray.splice(insertIndex, 0, newLessonData.type);

            // 🔹 Step 6: Update Firestore with new `lessons` and `lessontypes`
            const sectionDocRef = doc(db, `sections${language.languagecode}${language.sourcelanguagecode}`, section.id);
            await updateDoc(sectionDocRef, {
                lessons: updatedLessonsArray,
                lessontypes: updatedLessonTypesArray
            });

            console.log(`✅ Added lesson ${newLessonId} at index ${insertIndex} in section ${section.id}`);

            // 🔹 Step 7: Update UI state
            setLessons(prevLessons => {
                const updatedLessons = [...prevLessons, { id: newLessonId, ...newLessonData }];
                return updatedLessons.sort((a, b) => a.order - b.order); // ✅ Keep sorted
            });

            setSections(prevSections => {
                return prevSections.map(sec =>
                    sec.id === section.id
                        ? { ...sec, lessons: updatedLessonsArray, lessontypes: updatedLessonTypesArray }
                        : sec
                );
            });

            console.log("✅ UI state updated!");

        } catch (error) {
            console.error("❌ Error adding lesson:", error);
        }
    };

    const handleOpenEditDialog = (lesson) => {
        if (isLocked) return;
        const currentSection = sections.find(sec => sec.lessons.includes(lesson.id));
        setEditingLesson({
            ...lesson,
            sectionId: currentSection?.id || ""
        });
        setSelectedLessonSectionId(currentSection?.id || "");
        setTimeout(() => setOpenEditDialog(true), 100);
    };

    const handleSaveLesson = async () => {
        if (isLocked) return;
        if (!editingLesson) return;

        try {
            const updatedLesson = { ...editingLesson }
            const lessonDocRef = doc(db, `lessons${language.languagecode}${language.sourcelanguagecode}`, updatedLesson.id);

            // ✅ Update lesson data in Firestore
            await updateDoc(lessonDocRef, {
                explanation: updatedLesson.explanation,
                image: updatedLesson.image,
                languagecode: updatedLesson.languagecode,
                name: updatedLesson.name,
                order: updatedLesson.order,
                points: updatedLesson.points,
                sourcelanguagecode: updatedLesson.sourcelanguagecode,
                type: updatedLesson.type,
                vocabularycount: updatedLesson.vocabularycount
            });

            // ✅ Find current and new section
            const currentSection = sections.find(sec => sec.lessons.includes(updatedLesson.id));
            const newSection = sections.find(sec => sec.id === updatedLesson.sectionId);

            if (!newSection) throw new Error("New section not found");

            // ✅ Update sections if the section has changed
            if (currentSection?.id !== newSection.id) {
                const currentLessons = currentSection?.lessons.filter(id => id !== updatedLesson.id) || [];
                const currentTypes = currentSection?.lessontypes.filter((_, i) => currentSection.lessons[i] !== updatedLesson.id) || [];

                // Insert lesson ID at correct order position in new section
                const insertIndex = newSection.lessons.findIndex(lessonId => {
                    const lesson = lessons.find(l => l.id === lessonId);
                    return lesson && lesson.order > updatedLesson.order;
                });

                const newLessons = [...newSection.lessons];
                const newTypes = [...newSection.lessontypes];

                const idx = insertIndex === -1 ? newLessons.length : insertIndex;
                newLessons.splice(idx, 0, updatedLesson.id);
                newTypes.splice(idx, 0, updatedLesson.type);

                // ✅ Batch update both sections
                const batch = writeBatch(db);

                if (currentSection) {
                    const currentDocRef = doc(db, `sections${language.languagecode}${language.sourcelanguagecode}`, currentSection.id);
                    batch.update(currentDocRef, {
                        lessons: currentLessons,
                        lessontypes: currentTypes,
                    });
                }

                const newDocRef = doc(db, `sections${language.languagecode}${language.sourcelanguagecode}`, newSection.id);
                batch.update(newDocRef, {
                    lessons: newLessons,
                    lessontypes: newTypes,
                });

                await batch.commit();

                // ✅ Update local state
                setSections(prevSections =>
                    prevSections.map(sec => {
                        if (sec.id === currentSection?.id) return { ...sec, lessons: currentLessons, lessontypes: currentTypes };
                        if (sec.id === newSection.id) return { ...sec, lessons: newLessons, lessontypes: newTypes };
                        return sec;
                    })
                );
            }
            else {
                // ✅ Same section: still may need to update type and/or order
                const sec = currentSection; // same as newSection
                if (!sec) return;

                const oldIdx = sec.lessons.indexOf(updatedLesson.id);
                if (oldIdx === -1) return;

                // Update type at same index first
                let lessonsArr = [...sec.lessons];
                let typesArr = [...sec.lessontypes];
                typesArr[oldIdx] = updatedLesson.type;

                // Reorder if needed (remove then insert based on updatedLesson.order)
                // remove current
                lessonsArr.splice(oldIdx, 1);
                typesArr.splice(oldIdx, 1);

                const insertIndex = lessonsArr.findIndex(lessonId => {
                    const l = lessons.find(x => x.id === lessonId);
                    return l && l.order > updatedLesson.order;
                });

                const idx = insertIndex === -1 ? lessonsArr.length : insertIndex;
                lessonsArr.splice(idx, 0, updatedLesson.id);
                typesArr.splice(idx, 0, updatedLesson.type);

                // write to Firestore
                const secRef = doc(db, `sections${language.languagecode}${language.sourcelanguagecode}`, sec.id);
                await updateDoc(secRef, { lessons: lessonsArr, lessontypes: typesArr });

                // update local state
                setSections(prev =>
                    prev.map(s => (s.id === sec.id ? { ...s, lessons: lessonsArr, lessontypes: typesArr } : s))
                );
            }

            // ✅ Update lessons state
            setLessons(prevLessons =>
                prevLessons.map(l => (l.id === updatedLesson.id ? { ...l, ...updatedLesson } : l))
            );

            setOpenEditDialog(false);
        } catch (error) {
            console.error("❌ Error updating lesson:", error);
        }
    };

    const handleDeleteLesson = async (lessonId, section) => {
        if (isLocked) return;
        if (!window.confirm("Are you sure you want to delete this lesson?")) return;

        try {
            // 🔹 Step 1: Fetch all fields belonging to this lesson
            const fieldsDocName = `fields${language.languagecode}${language.sourcelanguagecode}`;
            const fieldsQuery = query(collection(db, fieldsDocName), where("lessonid", "==", lessonId));
            const fieldsSnap = await getDocs(fieldsQuery);

            // 🔹 Step 2: Delete each field document
            const deleteFieldPromises = fieldsSnap.docs.map(fieldDoc => deleteDoc(fieldDoc.ref));
            await Promise.all(deleteFieldPromises);

            console.log(`✅ Deleted ${fieldsSnap.docs.length} fields for lesson ${lessonId}`);

            // 🔹 Step 3: Delete the lesson
            const lessonsDocName = `lessons${language.languagecode}${language.sourcelanguagecode}`;
            await deleteDoc(doc(db, lessonsDocName, lessonId));

            console.log(`✅ Lesson ${lessonId} deleted successfully!`);

            // 🔹 Step 4: Remove lesson from the section state
            const updatedLessonsArray = section.lessons.filter(id => id !== lessonId);
            const updatedLessonTypesArray = section.lessontypes.filter((_, index) => section.lessons[index] !== lessonId);

            const sectionDocRef = doc(db, `sections${language.languagecode}${language.sourcelanguagecode}`, section.id);
            await updateDoc(sectionDocRef, {
                lessons: updatedLessonsArray,
                lessontypes: updatedLessonTypesArray
            });

            console.log(`✅ Updated section ${section.id} after lesson deletion`);

            // 🔹 Step 5: Update UI state
            setLessons(prevLessons => prevLessons.filter(l => l.id !== lessonId)); // ✅ Remove lesson from state
            setSections(prevSections =>
                prevSections.map(sec =>
                    sec.id === section.id
                        ? { ...sec, lessons: updatedLessonsArray, lessontypes: updatedLessonTypesArray }
                        : sec
                )
            );

            setLessonFields(prevFields => prevFields.filter(f => f.lessonid !== lessonId)); // ✅ Remove fields from state

        } catch (error) {
            console.error("❌ Error deleting lesson and fields:", error);
        }
    };

    const handleOpenFieldDialog = (field) => {
        if (isLocked) return;
        setSelectedField(field); // ✅ Update first
        setTimeout(() => setOpenFieldDialog(true), 100); // ✅ Open after React processes update
    };

    const handleSaveField = async () => {
        if (isLocked) return;
        if (!selectedField || !selectedField.id) return;

        try {
            const fieldDocRef = doc(
                db,
                `fields${language.languagecode}${language.sourcelanguagecode}`,
                selectedField.id
            );

            await updateDoc(fieldDocRef, {
                answer: selectedField.answer,
                audio: selectedField.audio,
                image: selectedField.image,
                lessonid: selectedField.lessonid,
                name: selectedField.name,
                order: selectedField.order,
                question: selectedField.question,
                repetition: selectedField.repetition,
                translation: selectedField.translation,
                type: selectedField.type
            });

            setLessonFields(prevFields =>
                prevFields.map(f => (f.id === selectedField.id ? selectedField : f))
            );

            setOpenFieldDialog(false);
            setSelectedField(null);
        } catch (error) {
            console.error("Error updating field:", error);
        }
    };

    const handleAddField = async (lessonId) => {
        if (isLocked) return;
        const lesson = lessons.find(l => l.id === lessonId);

        const newFieldData = {
            lessonid: lessonId,
            name: newFields[lessonId]?.name?.trim() || "Untitled Field", // ✅ Ensure non-empty value
            question: newFields[lessonId]?.question?.trim() || "", // ✅ Default empty string
            answer: newFields[lessonId]?.answer?.trim() || "",
            translation: newFields[lessonId]?.translation?.trim() || "",
            audio: newFields[lessonId]?.audio || "",
            image: newFields[lessonId]?.image || "",
            order: newFields[lessonId]?.order || 0,
            repetition: newFields[lessonId]?.repetition || 0,
            type: lesson.type || 1, // ✅ Default type if missing
        };

        try {
            const newDocRef = await addDoc(collection(db, `fields${language.languagecode}${language.sourcelanguagecode}`), newFieldData);

            setLessonFields(prevFields => [...prevFields, { id: newDocRef.id, ...newFieldData }]);

            console.log(`✅ Field ${newDocRef.id} added successfully!`);
        } catch (error) {
            console.error("❌ Error adding field:", error);
        }
    };

    const handleDeleteField = async (fieldId) => {
        if (isLocked) return;
        if (!window.confirm("Are you sure you want to delete this field?")) return;

        try {
            await deleteDoc(doc(db, `fields${language.languagecode}${language.sourcelanguagecode}`, fieldId));

            // 🔹 Remove field from UI
            setLessonFields(prevFields => prevFields.filter(f => f.id !== fieldId));

        } catch (error) {
            console.error("Error deleting field:", error);
        }
    };

    const normalizeText = (htmlString) => {
        if (!htmlString) return "";

        // 🔹 Strip HTML tags (e.g., <b>, <i>, etc.)
        const plainText = htmlString.replace(/<[^>]+>/g, "");

        return plainText
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // remove accents
            .replace(/[^\w\s]|_/g, "") // remove punctuation
            .replace(/\s+/g, "") // remove spaces
            .toLowerCase();
    };

    const organizeFields = async () => {
        if (isLocked) return;
        if (!window.confirm("Are you sure you want to update the fields?")) return;

        setOrganizing(true)

        try {
            const batch = writeBatch(db);
            const fieldsToUpdate = lessonFields.filter(f => f.name?.trim());

            fieldsToUpdate.forEach(field => {
                const normalizedAudio = field.translation !== "" ? normalizeText(field.name) : "";
                const fieldRef = doc(db, `fields${language.languagecode}${language.sourcelanguagecode}`, field.id);
                batch.update(fieldRef, { audio: normalizedAudio });
            });

            await batch.commit();

            // 🔹 Update local state
            setLessonFields(prev =>
                prev.map(f => f.name?.trim()
                    ? { ...f, audio: normalizeText(f.name) }
                    : f
                )
            );

            console.log(`✅ Set audio for ${fieldsToUpdate.length} fields.`);
        } catch (error) {
            console.error("❌ Error setting audio from name:", error);
        } finally {
            setOrganizing(false);
        }
    };

    return (
        <Box sx={{ padding: "20px" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginBottom: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                        onClick={() => navigate("/language", { state: { from: location.pathname } })}
                        sx={{ p: 0 }}
                    >
                        <Avatar
                            alt="Change Language"
                            src={require(`../assets/flags/${language.languagecode}flag.svg`)}
                        />
                    </IconButton>
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                    <Typography
                    variant="body1"
                    onClick={() => navigate("/settings", { state: { from: location.pathname } })}
                    sx={{ cursor: 'pointer' }}
                    >
                    {language.sourcelanguagecode.toUpperCase()}
                    </Typography>
                </Stack>

                {/* 🔹 Clear Audio Button */}
                <Button variant="contained" color="primary" onClick={organizeFields} disabled={organizing}
                    startIcon={organizing && <CircularProgress size={20} />}>
                    Organize All Fields
                </Button>
            </Stack>
            <Typography variant="h4" sx={{ marginBottom: 3, textAlign: "center" }}>Lessons Overview</Typography>
            <FormControl sx={{ minWidth: 140, marginBottom: 3 }}>
                <InputLabel id="level-select-label">Level</InputLabel>
                <Select
                    labelId="level-select-label"
                    value={selectedLevel}
                    label="Level"
                    onChange={(e) => setSelectedLevel(e.target.value)}
                >
                    {[1, 2, 3, 4, 5].map(level => (
                        <MenuItem key={level} value={level}>
                            Level {level}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Display Sections */}
            {sections
                .filter(section => section.level === selectedLevel)
                .map((section) => (
                    <Box key={section.id} sx={{ marginBottom: 5 }}>
                        {editingSection === section.id ? (
                            <Box sx={{ padding: 2, border: "1px solid #ddd", borderRadius: "10px", backgroundColor: "#f9f9f9" }}>
                                <TextField
                                    label="Section Name"
                                    fullWidth
                                    value={updatedSection.name}
                                    onChange={(e) => setUpdatedSection({ ...updatedSection, name: e.target.value })}
                                    sx={{ marginBottom: 2 }}
                                />

                                <TextField
                                    label="Level"
                                    fullWidth
                                    type="number"
                                    value={updatedSection.level}
                                    onChange={(e) => setUpdatedSection({ ...updatedSection, level: Number(e.target.value) })}
                                    sx={{ marginBottom: 2 }}
                                />

                                <TextField
                                    label="Order"
                                    fullWidth
                                    type="number"
                                    value={updatedSection.order}
                                    onChange={(e) => setUpdatedSection({ ...updatedSection, order: Number(e.target.value) })}
                                    sx={{ marginBottom: 2 }}
                                />

                                <TextField
                                    label="Image"
                                    fullWidth
                                    value={updatedSection.image}
                                    onChange={(e) => setUpdatedSection({ ...updatedSection, image: e.target.value })}
                                    sx={{ marginBottom: 2 }}
                                />

                                <Button variant="contained" color="primary" onClick={handleUpdateSection} sx={{ mr: 1 }}>
                                    Save
                                </Button>
                                <Button variant="outlined" color="secondary" onClick={() => setEditingSection(null)}>
                                    Cancel
                                </Button>
                            </Box>
                        ) : (
                            <>
                                <Stack direction="column" alignItems="center"
                                    justifyContent="center" spacing={1} sx={{ marginBottom: 2 }}>
                                    <Typography variant="h5">
                                        {section.name} (Level {section.level})
                                    </Typography>
                                    <img
                                        src={getImage(section.image)}
                                        alt={section.image}
                                        width={256}
                                        height={256}
                                        style={{
                                            objectFit: 'contain',
                                            borderRadius: 4
                                        }}
                                    />
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => { if (!isLocked) setOpenAddSection(true) }}
                                            sx={{ width: "auto", minWidth: "fit-content" }}
                                        >
                                            + Add New Section
                                        </Button>

                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => handleEditSection(section)}
                                            sx={{ width: "auto", minWidth: "fit-content" }}
                                        >
                                            Edit Section
                                        </Button>

                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() => handleDeleteSection(section.id)}
                                            sx={{ width: "auto", minWidth: "fit-content" }}
                                        >
                                            Delete Section
                                        </Button>
                                    </Stack>
                                </Stack>

                                {/* Lesson Cards */}
                                <Grid container spacing={2}>
                                    {section.lessons
                                        .map(lessonId => lessons.find(l => l.id === lessonId))
                                        .filter(lesson => lesson)
                                        .sort((a, b) => a.order - b.order)
                                        .map(lesson => (
                                            <LessonCard
                                                key={lesson.id}
                                                languagecode={language.languagecode}
                                                lesson={lesson}
                                                fields={lessonFields
                                                    .filter(f => f.lessonid === lesson.id && f.level === selectedLevel.level)
                                                    .sort((a, b) => a.order - b.order)}
                                                newFields={newFields}
                                                setNewFields={setNewFields}
                                                onEditLesson={handleOpenEditDialog}
                                                onDeleteLesson={(lessonId) => handleDeleteLesson(lessonId, section)}
                                                onEditField={handleOpenFieldDialog}
                                                onDeleteField={handleDeleteField}
                                                onAddField={handleAddField}
                                            />
                                        ))}

                                    {/* 🔹 Add New Lesson Button */}
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <Card
                                            sx={{ minHeight: 180, backgroundColor: "#e0e0e0", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                            onClick={() => handleAddLesson(section)}
                                        >
                                            <CardContent>
                                                <Typography variant="h6" color="primary" textAlign="center">
                                                    + Add Lesson
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </Box>
                ))}
            <Dialog open={openAddSection} onClose={() => setOpenAddSection(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add New Section</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Section Name"
                        fullWidth
                        value={newSection.name}
                        onChange={(e) => setNewSection({ ...newSection, name: e.target.value })}
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Level"
                        fullWidth
                        type="number"
                        value={newSection.level}
                        onChange={(e) => setNewSection({ ...newSection, level: Number(e.target.value) })}
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Order"
                        fullWidth
                        type="number"
                        value={newSection.order}
                        onChange={(e) => setNewSection({ ...newSection, order: Number(e.target.value) })}
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Image"
                        fullWidth
                        value={newSection.image}
                        onChange={(e) => setNewSection({ ...newSection, image: e.target.value })}
                        sx={{ marginBottom: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddSection(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleAddSection} color="primary" variant="contained">
                        Add Section
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Lesson</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Lesson Name"
                        fullWidth
                        value={editingLesson?.name || ""}
                        onChange={(e) => setEditingLesson(prev => ({ ...prev, name: e.target.value }))}
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Explanation"
                        fullWidth
                        multiline
                        rows={3}
                        value={editingLesson?.explanation || ""}
                        onChange={(e) => setEditingLesson(prev => ({ ...prev, explanation: e.target.value }))}
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Lesson Order"
                        fullWidth
                        type="number"
                        value={editingLesson?.order || 0}
                        onChange={(e) => setEditingLesson(prev => ({ ...prev, order: Number(e.target.value) }))}
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Image"
                        fullWidth
                        value={editingLesson?.image || ""}
                        onChange={(e) => setEditingLesson(prev => ({ ...prev, image: e.target.value }))}
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Points"
                        fullWidth
                        type="number"
                        value={editingLesson?.points || 0}
                        onChange={(e) => setEditingLesson(prev => ({ ...prev, points: Number(e.target.value) }))}
                        sx={{ marginBottom: 2 }}
                    />

                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={editingLesson?.type || 1}
                            onChange={(e) => setEditingLesson(prev => ({ ...prev, type: Number(e.target.value) }))}
                            label="Type"
                        >
                            <MenuItem value={1}>Gramer</MenuItem>
                            <MenuItem value={2}>Kelimeler</MenuItem>
                            <MenuItem value={3}>Konuşmalar</MenuItem>
                            <MenuItem value={4}>Tekrar</MenuItem>
                            <MenuItem value={5}>Genel Tekrar</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <InputLabel>Section</InputLabel>
                        <Select
                            value={editingLesson?.sectionId || ""}
                            onChange={(e) => setEditingLesson(prev => ({ ...prev, sectionId: e.target.value }))}
                            label="Section"
                        >
                            {sections.map((sec) => (
                                <MenuItem key={sec.id} value={sec.id}>
                                    {sec.name} (Level {sec.level})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Vocabulary Count"
                        fullWidth
                        type="number"
                        value={editingLesson?.vocabularycount || 0}
                        onChange={(e) => setEditingLesson(prev => ({ ...prev, vocabularycount: Number(e.target.value) }))}
                        sx={{ marginBottom: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveLesson} color="primary" variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openFieldDialog} onClose={() => setOpenFieldDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Field</DialogTitle>
                <DialogContent>  
                    <TextField
                        label="Field Name"
                        fullWidth
                        value={selectedField?.name || ""}
                        onChange={(e) =>
                            setSelectedField(prev => {
                                if (!prev) return prev;
                                return { ...prev, name: e.target.value };
                            })
                        }
                        sx={{ marginTop: 3, marginBottom: 2 }}
                    />

                    <TextField
                        label="Order"
                        fullWidth
                        type="number"
                        value={selectedField?.order || 0}
                        onChange={(e) =>
                            setSelectedField(prev => ({ ...prev, order: Number(e.target.value) }))
                        }
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Question"
                        fullWidth
                        multiline
                        rows={2}
                        value={selectedField?.question || ""}
                        onChange={(e) =>
                            setSelectedField(prev => ({ ...prev, question: e.target.value }))
                        }
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Answer"
                        fullWidth
                        value={selectedField?.answer || ""}
                        onChange={(e) =>
                            setSelectedField(prev => ({ ...prev, answer: e.target.value }))
                        }
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Translation"
                        fullWidth
                        value={selectedField?.translation || ""}
                        onChange={(e) =>
                            setSelectedField(prev => ({ ...prev, translation: e.target.value }))
                        }
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Audio"
                        fullWidth
                        value={selectedField?.audio || ""}
                        onChange={(e) =>
                            setSelectedField(prev => ({ ...prev, audio: e.target.value }))
                        }
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Image"
                        fullWidth
                        value={selectedField?.image || ""}
                        onChange={(e) =>
                            setSelectedField(prev => ({ ...prev, image: e.target.value }))
                        }
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Repetition"
                        fullWidth
                        type="number"
                        value={selectedField?.repetition || 0}
                        onChange={(e) =>
                            setSelectedField(prev => ({ ...prev, repetition: Number(e.target.value) }))
                        }
                        sx={{ marginBottom: 2 }}
                    />

                    <TextField
                        label="Type"
                        fullWidth
                        type="number"
                        value={selectedField?.type || 1}
                        onChange={(e) =>
                            setSelectedField(prev => ({ ...prev, type: Number(e.target.value) }))
                        }
                        sx={{ marginBottom: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setOpenFieldDialog(false);
                        setSelectedField(null);
                    }} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveField} color="primary" variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}


